import React, { useEffect } from 'react';
import { Movie } from '../types';
import {
  generateMetaDescription,
  generateKeywords,
  generateMovieStructuredData,
  generateOpenGraphTags,
  generateTwitterTags,
  generateCanonicalUrl
} from '../services/seoService';

interface SEOProps {
  movie?: Movie;
  path: string;
}

const SEO: React.FC<SEOProps> = ({ movie, path }) => {
  const title = movie 
    ? `${movie.title} - Watch and Download in HD | Movies4Load`
    : 'Movies4Load - Watch and Download Latest Movies in HD Quality';

  const metaDescription = generateMetaDescription(movie);
  const keywords = generateKeywords(movie);
  const openGraphTags = generateOpenGraphTags(movie);
  const twitterTags = generateTwitterTags(movie);
  const canonicalUrl = generateCanonicalUrl(path);
  const structuredData = movie ? generateMovieStructuredData(movie) : null;

  useEffect(() => {
    // Update document title
    document.title = title;

    // Update meta tags
    const updateMetaTag = (name: string, content: string) => {
      let element = document.querySelector(`meta[name="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('name', name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    const updatePropertyMetaTag = (property: string, content: string) => {
      let element = document.querySelector(`meta[property="${property}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('property', property);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Basic meta tags
    updateMetaTag('description', metaDescription);
    updateMetaTag('keywords', keywords);

    // Open Graph tags
    Object.entries(openGraphTags).forEach(([property, content]) => {
      updatePropertyMetaTag(property, content);
    });

    // Twitter Card tags
    Object.entries(twitterTags).forEach(([name, content]) => {
      updateMetaTag(name, content);
    });

    // Canonical URL
    let canonicalElement = document.querySelector('link[rel="canonical"]');
    if (!canonicalElement) {
      canonicalElement = document.createElement('link');
      canonicalElement.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalElement);
    }
    canonicalElement.setAttribute('href', canonicalUrl);

    // Structured Data
    let scriptElement = document.querySelector('script[type="application/ld+json"]');
    if (structuredData) {
      if (!scriptElement) {
        scriptElement = document.createElement('script');
        scriptElement.setAttribute('type', 'application/ld+json');
        document.head.appendChild(scriptElement);
      }
      scriptElement.textContent = JSON.stringify(structuredData);
    } else if (scriptElement) {
      scriptElement.remove();
    }

    // Cleanup function
    return () => {
      // Remove structured data on unmount
      const structuredDataScript = document.querySelector('script[type="application/ld+json"]');
      if (structuredDataScript) {
        structuredDataScript.remove();
      }
    };
  }, [title, metaDescription, keywords, openGraphTags, twitterTags, canonicalUrl, structuredData]);

  return null;
};

export default SEO; 